import React from 'react';
import {
  Clock,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Star,
  MoreVertical,
  Plus,
  Play,
  CheckSquare,
} from 'lucide-react';

const COLUMNS = [
  { id: 'BACKLOG', title: 'Backlog', color: '#94a3b8' },
  { id: 'TODO', title: 'To Do', color: '#3b82f6' },
  { id: 'IN_PROGRESS', title: 'In Progress', color: '#06b6d4' },
  { id: 'REVIEW', title: 'In Review', color: '#a855f7' },
  { id: 'BLOCKED', title: 'Blocked', color: '#ef4444' },
  { id: 'COMPLETED', title: 'Completed', color: '#10b981' },
];

export default function TaskKanbanBoard({
  tasks = [],
  onTaskClick,
  onStatusChange,
  onQuickAdd,
}) {
  const now = new Date().getTime();

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, minmax(280px, 1fr))',
        gap: '16px',
        overflowX: 'auto',
        paddingBottom: '16px',
        minHeight: '550px',
      }}
    >
      {COLUMNS.map((col) => {
        const colTasks = tasks.filter((t) => (t.status || 'TODO') === col.id);

        return (
          <div
            key={col.id}
            style={{
              background: 'var(--bg-surface)',
              borderRadius: '12px',
              border: '1px solid var(--border-color)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'calc(100vh - 230px)',
              overflow: 'hidden',
            }}
          >
            {/* Column Header */}
            <div
              style={{
                padding: '14px 16px',
                borderBottom: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'rgba(255, 255, 255, 0.02)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: col.color,
                  }}
                />
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {col.title}
                </span>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '1px 6px',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  {colTasks.length}
                </span>
              </div>

              {onQuickAdd && (
                <button
                  onClick={() => onQuickAdd(col.id)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                  }}
                  title={`Add task to ${col.title}`}
                >
                  <Plus size={16} />
                </button>
              )}
            </div>

            {/* Cards Container */}
            <div
              style={{
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                overflowY: 'auto',
                flex: 1,
              }}
            >
              {colTasks.length === 0 ? (
                <div
                  style={{
                    padding: '24px 12px',
                    textAlign: 'center',
                    color: 'var(--text-secondary)',
                    fontSize: '12px',
                    border: '1px dashed var(--border-color)',
                    borderRadius: '8px',
                  }}
                >
                  No tasks
                </div>
              ) : (
                colTasks.map((task) => {
                  const isOverdue =
                    task.end_at &&
                    new Date(task.end_at).getTime() < now &&
                    task.status !== 'COMPLETED';

                  return (
                    <div
                      key={task.id}
                      onClick={() => onTaskClick && onTaskClick(task)}
                      style={{
                        background: 'var(--bg-secondary)',
                        borderRadius: '10px',
                        border: '1px solid var(--border-color)',
                        padding: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'transform 0.15s, border-color 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--accent-primary)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border-color)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {/* Priority and Project info */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            textTransform: 'uppercase',
                            background:
                              task.priority === 'URGENT'
                                ? 'rgba(239, 68, 68, 0.2)'
                                : task.priority === 'HIGH'
                                ? 'rgba(245, 158, 11, 0.2)'
                                : 'rgba(59, 130, 246, 0.15)',
                            color:
                              task.priority === 'URGENT'
                                ? 'var(--danger)'
                                : task.priority === 'HIGH'
                                ? 'var(--warning)'
                                : 'var(--accent-primary)',
                          }}
                        >
                          {task.priority}
                        </span>

                        {task.is_favorite && (
                          <Star size={14} fill="#eab308" color="#eab308" />
                        )}
                      </div>

                      {/* Title */}
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          lineHeight: 1.4,
                        }}
                      >
                        {task.title}
                      </div>

                      {/* Project badge if present */}
                      {task.project_name && (
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                          📁 {task.project_name}
                        </div>
                      )}

                      {/* Subtasks Progress */}
                      {task.subtasks_total > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <CheckSquare size={12} color="var(--text-secondary)" />
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            {task.subtasks_completed}/{task.subtasks_total} ({task.progress}%)
                          </span>
                          <div style={{ flex: 1, height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
                            <div
                              style={{
                                height: '100%',
                                width: `${task.progress}%`,
                                background: task.progress === 100 ? 'var(--success)' : 'var(--accent-primary)',
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Tags */}
                      {task.tags && task.tags.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {task.tags.slice(0, 3).map((t) => (
                            <span
                              key={t}
                              style={{
                                fontSize: '10px',
                                background: 'rgba(255,255,255,0.06)',
                                color: 'var(--text-secondary)',
                                padding: '1px 5px',
                                borderRadius: '4px',
                              }}
                            >
                              #{t}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Card Footer: Due date & status move */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginTop: '4px',
                          paddingTop: '6px',
                          borderTop: '1px solid rgba(255,255,255,0.05)',
                        }}
                      >
                        {task.end_at ? (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '11px',
                              color: isOverdue ? 'var(--danger)' : 'var(--text-secondary)',
                              fontWeight: isOverdue ? 600 : 400,
                            }}
                          >
                            <Calendar size={12} />
                            <span>{new Date(task.end_at).toLocaleDateString()}</span>
                          </div>
                        ) : (
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>No date</div>
                        )}

                        {/* Quick move dropdown */}
                        <select
                          onClick={(e) => e.stopPropagation()}
                          value={task.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (onStatusChange) onStatusChange(task.id, e.target.value);
                          }}
                          style={{
                            background: 'transparent',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            color: 'var(--text-secondary)',
                            fontSize: '10px',
                            padding: '2px 4px',
                            cursor: 'pointer',
                          }}
                        >
                          {COLUMNS.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
